import { FC, RefObject, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { tzEndDay, tzStartDay } from 'utils/helper';
import { ReactComponent as IconArrowLeft } from '../../assets/arrow_left.svg';
import { ReactComponent as IconArrowRight } from '../../assets/arrow_right.svg';
import { ReactComponent as IconDoubleArrowLeft } from '../../assets/double_arrow_left.svg';
import { ReactComponent as IconDoubleArrowRight } from '../../assets/double_arrow_right.svg';
import styles from './DatePicker.module.scss';

interface DatePickerProps {
  datePickerRef?: RefObject<ReactDatePicker<undefined, undefined>>;
  minDate: Date | undefined;
  maxDate: Date | undefined;
  excludedDates?: Date[] | undefined;
  date?: Date;
  onChange: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  isLoading?: boolean;
  disable?: boolean;
  filterDate?: () => boolean;
  dayClassName?: (date: Date) => string;
}

const DatePicker: FC<DatePickerProps> = ({
  datePickerRef,
  minDate: _minDate,
  maxDate: _maxDate,
  excludedDates,
  date,
  onChange,
  onMonthChange,
  isLoading,
  disable,
  filterDate,
  dayClassName,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(date);
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();

  useEffect(() => {
    _minDate && setMinDate(_minDate);
  }, [_minDate]);

  useEffect(() => {
    _maxDate && setMaxDate(_maxDate);
  }, [_maxDate]);

  useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  return (
    <ReactDatePicker
      ref={datePickerRef}
      selected={selectedDate ? tzStartDay(selectedDate) : selectedDate}
      onChange={(date) => date && onChange(date)}
      inline
      minDate={minDate ? tzStartDay(minDate) : minDate}
      maxDate={maxDate ? tzEndDay(maxDate) : maxDate}
      filterDate={filterDate}
      excludeDates={excludedDates}
      dayClassName={isLoading ? () => 'isLoading' : dayClassName}
      formatWeekDay={(weekDay: string) => t(`weekdays.${weekDay}` as any)}
      renderDayContents={(day: number, date: Date) =>
        isLoading &&
        (minDate ? tzStartDay(date) >= tzStartDay(minDate) : true) &&
        (maxDate ? tzEndDay(date) <= tzEndDay(maxDate) : true) ? (
          <Skeleton width={40} height={40} borderRadius={'50%'} />
        ) : (
          <span>{day}</span>
        )
      }
      renderCustomHeader={({
        decreaseYear,
        increaseYear,
        decreaseMonth,
        increaseMonth,
        prevYearButtonDisabled,
        nextYearButtonDisabled,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        date,
      }) => {
        const isPrevYearDisabled =
          !minDate ||
          (!!minDate &&
            minDate.getFullYear() * 12 + minDate.getMonth() > date.getFullYear() * 12 + date.getMonth() - 12) ||
          disable;
        const isNextYearDisabled =
          !maxDate ||
          (!!maxDate &&
            maxDate.getFullYear() * 12 + maxDate.getMonth() < date.getFullYear() * 12 + date.getMonth() + 12) ||
          disable;
        return (
          <div className={styles.customHeader}>
            {
              <button
                type="button"
                onClick={() => {
                  onMonthChange(new Date(date.getFullYear() - 1, date.getMonth(), 1));
                  decreaseYear();
                }}
                disabled={prevYearButtonDisabled || isPrevYearDisabled}
                className={styles.doubleArrowLeft}>
                <IconDoubleArrowLeft />
              </button>
            }
            {
              <button
                type="button"
                onClick={() => {
                  onMonthChange(new Date(date.getFullYear(), date.getMonth() - 1, 1));
                  decreaseMonth();
                }}
                disabled={prevMonthButtonDisabled || !minDate || disable}>
                <IconArrowLeft />
              </button>
            }
            <div className={styles.title}>
              {date
                .toLocaleString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                })
                .replace('年', '年 ')}
            </div>

            <button
              type="button"
              onClick={() => {
                onMonthChange(new Date(date.getFullYear(), date.getMonth() + 1, 1));
                increaseMonth();
              }}
              disabled={nextMonthButtonDisabled || !maxDate || disable}>
              <IconArrowRight />
            </button>
            <button
              type="button"
              onClick={() => {
                onMonthChange(new Date(date.getFullYear() + 1, date.getMonth(), 1));
                increaseYear();
              }}
              disabled={nextYearButtonDisabled || isNextYearDisabled}
              className={styles.doubleArrowRight}>
              <IconDoubleArrowRight />
            </button>
          </div>
        );
      }}
    />
  );
};

export default DatePicker;
